import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import type { ApiError } from "@/lib/api/client";
import { routeTree } from "./routeTree.gen";

/**
 * Retries transport failures, never the server's answer.
 *
 * A 4xx is a decision — unauthorized, not found, malformed, rate limited — and asking again
 * cannot change it. Retrying one is pure waste, and for 429 it is actively harmful: the retry
 * ladder spends the caller's remaining budget re-asking a limiter that has already said no,
 * turning one rejection into three and pushing the window further out. 5xx and network errors
 * are the opposite — they may be transient, so those still get two backed-off retries.
 */
export const retryOnlyTransportFailures = (failureCount: number, error: unknown) => {
  const status = (error as Partial<ApiError> | null | undefined)?.status;
  if (typeof status === "number" && status >= 400 && status < 500) return false;
  return failureCount < 2;
};

/**
 * Global QueryClient defaults — applied to EVERY query in the app.
 * This is the single source of truth for deduplication behavior.
 *
 * `staleTime` is a short dedup window, not a cache-invalidation strategy: a mutation that
 * calls `invalidateQueries` refetches its affected keys immediately regardless of this value,
 * so freshly created or edited rows still appear at once. The window only collapses the
 * repeat fetches of the SAME key that a burst of navigation would otherwise produce.
 *
 * Queries with a genuine real-time requirement opt out per-tier in `lib/api/query-options.ts`
 * (`STUDENT_QUERY_OPTIONS` / `OPERATIONAL_QUERY_OPTIONS`), which override these defaults.
 */
const QUERY_CLIENT_DEFAULTS = {
  queries: {
    staleTime: 30_000, // Dedup window: repeat fetches of one key within 30s serve from cache
    gcTime: 5 * 60 * 1000, // 5 min cache retention for smooth transition smoothing
    refetchOnWindowFocus: false, // Alt-tabbing should not refetch; real-time tiers opt back in
    refetchOnMount: true, // Refetch on mount whenever query is stale
    refetchOnReconnect: true, // Refetch when network connection is restored
    retry: retryOnlyTransportFailures,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    networkMode: "online" as const,
  },
  mutations: {
    // Never auto-repeat a write. One click must produce one request — a retried login burns
    // two attempts from a limiter partitioned by email, and a retried POST can double-create.
    retry: false,
    networkMode: "online" as const,
  },
};

export const getRouter = () => {
  // QueryClient MUST be created inside getRouter() for TanStack Start SSR isolation.
  // Each server request gets its own cache. The client gets one persistent instance.
  const queryClient = new QueryClient({ defaultOptions: QUERY_CLIENT_DEFAULTS });

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
  });

  return router;
};
